import type { InterpretationModel } from '@/entities/optimization-statistic/@x/pricing-campaign'
import type {
	FailedPendingResponse,
	GlobalPCSetModel,
	HTTPValidationError,
	PricingCampaignListParams,
	PricingCampaignMatchedProductsCountResponseModel,
	PricingCampaignModel,
	PricingCampaignOverviewListParams,
	PricingCampaignOverviewModel,
	PricingCampaignProductCountReorderRequestModel,
	PricingCampaignProductCountRequestModel,
	ProductsByEntityRequestModel,
	ProductsByPCModel,
} from '@/shared/generated'
import type { UseQueryOptions } from '@/shared/lib/query-client'
import type { AxiosError } from 'axios'

import { useQuery } from '@tanstack/react-query'
import { find } from 'lodash/fp'

import { interpretationSchema } from '@/entities/optimization-statistic/@x/pricing-campaign'

import { client } from '@/shared/api/client'
import { cyrb53 } from '@/shared/tools/utils'

export const pricingCampaignsQueries = {
	list: (queryParams: PricingCampaignListParams = {}) => ({
		queryKey: ['pricing-campaigns', queryParams],
		queryFn: () => client.pricingCampaign.pricingCampaignList(queryParams),
	}),
	overview: (queryParams: PricingCampaignOverviewListParams = {}) => ({
		queryKey: ['pricing-campaigns-overview', queryParams],
		queryFn: () =>
			client.pricingCampaign.pricingCampaignOverviewList(queryParams),
	}),
	single: (pricingCampaignId: string) => ({
		queryKey: ['pricing-campaigns', {}, pricingCampaignId],
		queryFn: () =>
			client.pricingCampaign.pricingCampaignGet({ pricingCampaignId }),
	}),
	listPcSets: () => ({
		queryKey: ['global-pc-sets-list'],
		queryFn: () => client.globalPcSet.globalPcSetList(),
	}),
	singlePcSet: (globalPcSetId: string) => ({
		queryKey: ['global-pc-set', globalPcSetId],
		queryFn: () => client.globalPcSet.globalPcSetGet({ globalPcSetId }),
	}),
	mainPcSet: () => ({
		queryKey: ['main-global-pc-set'],
		queryFn: () =>
			client.globalPcSet
				.globalPcSetList()
				.then(find(({ is_main }) => Boolean(is_main))),
	}),
}

/**
 * Queries
 */
export const usePricingCampaignsOverviewQuery = <
	T = PricingCampaignOverviewModel[],
>(
	queryParams: PricingCampaignOverviewListParams = {},
	options?: UseQueryOptions<PricingCampaignOverviewModel[], Error, T>
) =>
	useQuery<PricingCampaignOverviewModel[], Error, T>({
		...pricingCampaignsQueries.overview(queryParams),
		...(options || {}),
	})

export const usePricingCampaignsQuery = <T = PricingCampaignModel[]>(
	queryParams: PricingCampaignListParams = {},
	options?: UseQueryOptions<PricingCampaignModel[], Error, T>
) =>
	useQuery<PricingCampaignModel[], Error, T>({
		...pricingCampaignsQueries.list(queryParams),
		...(options || {}),
	})
export const usePricingCampaignQuery = <T = PricingCampaignModel>(
	pricingCampaignId: string,
	options?: UseQueryOptions<PricingCampaignModel, Error, T>
) =>
	useQuery<PricingCampaignModel, Error, T>({
		...pricingCampaignsQueries.single(pricingCampaignId),
		...(options || {}),
	})

export const useMatchedProductsQuery = <
	T = PricingCampaignMatchedProductsCountResponseModel[],
>(
	data: PricingCampaignProductCountReorderRequestModel,
	options?: UseQueryOptions<
		PricingCampaignMatchedProductsCountResponseModel[],
		AxiosError<HTTPValidationError | FailedPendingResponse>,
		T
	>
) =>
	useQuery<
		PricingCampaignMatchedProductsCountResponseModel[],
		AxiosError<HTTPValidationError | FailedPendingResponse>,
		T
	>({
		queryKey: ['matched-products', cyrb53(JSON.stringify(data))],
		queryFn: () =>
			client.pricingCampaign.pricingCampaignMatchedProductsReorder(data),
		...(options || {}),
	})

export const useMatchedProductsOnEditPcQuery = <
	T = PricingCampaignMatchedProductsCountResponseModel,
>(
	data: PricingCampaignProductCountRequestModel,
	options?: UseQueryOptions<
		PricingCampaignMatchedProductsCountResponseModel,
		AxiosError<HTTPValidationError | FailedPendingResponse>,
		T
	>
) =>
	useQuery<
		PricingCampaignMatchedProductsCountResponseModel,
		AxiosError<HTTPValidationError | FailedPendingResponse>,
		T
	>({
		queryKey: ['matched-products', cyrb53(JSON.stringify(data))],
		queryFn: () => client.pricingCampaign.pricingCampaignMatchedProducts(data),
		...(options || {}),
	})
export const useProductsCountsByPcQuery = <T = ProductsByPCModel[]>(
	body: ProductsByEntityRequestModel,
	options?: UseQueryOptions<ProductsByPCModel[], Error, T>
) =>
	useQuery<ProductsByPCModel[], Error, T>({
		queryKey: ['products-counts-by-pc', body],
		queryFn: () => client.products.getProductsCountByPc(body),
		...(options || {}),
	})
export const usePricingCampaignLimitsExampleQuery = <T = InterpretationModel>(
	body: any,
	options?: UseQueryOptions<InterpretationModel, AxiosError, T>
) =>
	useQuery<InterpretationModel, AxiosError, T>({
		queryKey: ['pricing-campaign-limits-example', body],
		queryFn: () =>
			client.settingsInterpretation
				.priceLimits(body)
				.then(interpretationSchema.parse),
		retry: false,
		...(options || {}),
	})

export const usePricingCampaignAnchoringExampleQuery = <
	T = InterpretationModel,
>(
	body: any,
	options?: UseQueryOptions<InterpretationModel, AxiosError, T>
) =>
	useQuery<InterpretationModel, AxiosError, T>({
		queryKey: ['pricing-campaign-anchoring-example', body],
		queryFn: () =>
			client.settingsInterpretation
				.anchoring(body)
				.then(interpretationSchema.parse),
		retry: false,
		...(options || {}),
	})

export const usePricingCampaignRoundingExampleQuery = <T = InterpretationModel>(
	body: any,
	options?: UseQueryOptions<InterpretationModel, AxiosError, T>
) =>
	useQuery<InterpretationModel, AxiosError, T>({
		queryKey: ['pricing-campaign-rounding-example', body],
		queryFn: () =>
			client.settingsInterpretation
				.rounding(body)
				.then(interpretationSchema.parse),
		retry: false,
		...(options || {}),
	})

export const useGlobalPcSetsQuery = <T = GlobalPCSetModel[]>(
	options?: UseQueryOptions<
		GlobalPCSetModel[],
		AxiosError<HTTPValidationError>,
		T
	>
) =>
	useQuery<GlobalPCSetModel[], AxiosError<HTTPValidationError>, T>({
		...pricingCampaignsQueries.listPcSets(),
		...(options || {}),
	})

export const useGlobalPcSetQuery = <T = GlobalPCSetModel>(
	globalPcSetId: string,
	options?: UseQueryOptions<
		GlobalPCSetModel,
		AxiosError<HTTPValidationError>,
		T
	>
) =>
	useQuery<GlobalPCSetModel, AxiosError<HTTPValidationError>, T>({
		...pricingCampaignsQueries.singlePcSet(globalPcSetId),
		...(options || {}),
	})

export const useMainGlobalPcSetQuery = <T = GlobalPCSetModel | undefined>(
	options?: UseQueryOptions<
		GlobalPCSetModel | undefined,
		AxiosError<HTTPValidationError>,
		T
	>
) =>
	useQuery<GlobalPCSetModel | undefined, AxiosError<HTTPValidationError>, T>({
		...pricingCampaignsQueries.mainPcSet(),
		...(options || {}),
	})
